import React from 'react';
import './MapLegend.css';
import type { ChoroplethBin } from '../../utils/choroplethDesignSystem';
import { CHOROPLETH_NO_DATA_FILL } from '../../utils/choroplethDesignSystem';
import type { MapVariableId } from '../../utils/genericMapClassification';
import { formatMapReferenceValue } from '../../utils/mapTractStatistics';

export interface LegendConfig {
  title: string;
  dataYear: string;
  colorScale: string[];
  breaks: number[];
  unit: string;
  tractCount: number;
  referenceValue?: { label: string; value: number };
}

export type MapLegendProps = LegendConfig & {
  bins?: ChoroplethBin[];
  breakContext?: string;
  directionHint?: string;
  mapVariableId?: MapVariableId;
};

const MapLegend: React.FC<MapLegendProps> = ({
  title,
  dataYear,
  unit,
  tractCount,
  referenceValue,
  bins = [],
  breakContext,
  directionHint = 'Darker = more',
  mapVariableId,
}) => {
  const showUnitInTitleOnly = unit === ' MT CO₂e/HH';

  const formatPoliticalLean = (value: number): string => {
    if (value > 0) return `D +${value.toFixed(1)}`;
    if (value < 0) return `R +${Math.abs(value).toFixed(1)}`;
    return 'Even';
  };

  const legendItems =
    bins.length > 0
      ? bins.map((b) => ({ color: b.color, label: b.label }))
      : [];

  return (
    <div className="map-legend-root">
      <div className="map-legend-header">
        <h4 className="map-legend-title">
          {title}
          {showUnitInTitleOnly && ` (${unit.trim()})`}
        </h4>
        <p className="map-legend-year">{dataYear}</p>
        {directionHint && title !== 'Political Lean' ? (
          <p className="map-legend-year">{directionHint}</p>
        ) : null}
        {breakContext ? <p className="map-legend-year">{breakContext}</p> : null}
      </div>

      <div className="map-legend-items">
        {legendItems.map((item, index) => (
          <div key={index} className="map-legend-item">
            <div
              className="map-legend-swatch map-legend-swatch-data"
              style={{ backgroundColor: item.color }}
            />
            <span className="map-legend-label">{item.label}</span>
          </div>
        ))}
      </div>

      <div className="map-legend-footer">
        {referenceValue && (
          <div>
            {referenceValue.label}:{' '}
            {mapVariableId
              ? formatMapReferenceValue(mapVariableId, referenceValue.value, { unit, title })
              : title === 'Political Lean'
                ? referenceValue.value > 0
                  ? `D +${referenceValue.value.toFixed(1)}`
                  : referenceValue.value < 0
                    ? `R +${Math.abs(referenceValue.value).toFixed(1)}`
                    : 'Even'
                : unit === ' min'
                  ? `${referenceValue.value.toFixed(1)}${unit}`
                  : unit === ' MT CO₂e/HH'
                    ? `${referenceValue.value.toFixed(1)}${unit}`
                    : unit === '%'
                      ? `${referenceValue.value.toFixed(1)}${unit}`
                      : unit === 'count'
                        ? referenceValue.value.toFixed(1)
                        : unit === '$'
                          ? `$${Math.round(referenceValue.value).toLocaleString()}`
                          : `${referenceValue.value.toLocaleString(undefined, { maximumFractionDigits: 2 })}${unit}`}
          </div>
        )}
        <div>{tractCount.toLocaleString()} census tracts</div>
        <div className="map-legend-divider">
          <div className="map-legend-nodata">
            <div
              className="map-legend-swatch map-legend-swatch-nodata"
              style={{ backgroundColor: CHOROPLETH_NO_DATA_FILL }}
            />
            <span>No data</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapLegend;
